import type { NurseShiftDraft } from "../../utils/buildNurseShift";
import { scheduleSlotDay } from "../../utils/departmentWeeks";
import { formatPersonFullName } from "../../utils/formatters";
import type { Job, Nurse, NurseInShift, OpenShiftRequest, ShiftRequest, WeekData } from "../../utils/models";
import { NurseTrainingStatus } from "../../utils/models";
import { findRedundantOpenShift, isOpenShiftRequest } from "../../utils/openShiftMatching";
import type { ShiftAvailabilityWindow } from "../../utils/shiftAvailability";
import type { ShiftAssignment } from "./addStaffUtils";

/** Analytics source of every add made from the Add Staff panel. */
export const ADD_STAFF_ANALYTICS_SOURCE = "add_nurse_dialog";

/** The shift slot and details every nurse of one add lands in. */
export type AddStaffTarget = {
	window: ShiftAvailabilityWindow;
	weekIndex: number;
	dayNo: number;
	shiftNo: number;
	/** `""` = no location. */
	room: string;
	/** The step-1 job; `undefined` in role-mode departments. */
	jobId: string | undefined;
	assignment: ShiftAssignment;
};

/** The slot-and-details half of an add, as both the single add's args and a multi-add request carry it. */
type AddStaffSlot = Omit<AddStaffTarget, "window">;

/** The add's target from whichever input holds it — one mapping, so the single and multi paths can't drift. */
export const toAddStaffTarget = ({ weekIndex, dayNo, shiftNo, room, jobId, assignment }: AddStaffSlot, window: ShiftAvailabilityWindow): AddStaffTarget => ({
	window,
	weekIndex,
	dayNo,
	shiftNo,
	room,
	jobId,
	assignment,
});

export type PreparedShiftDeps = {
	/** The panel's schedule weeks; the target's positions are in them. */
	weeks: WeekData[];
	prepareNurseShift: (draft: NurseShiftDraft) => NurseInShift;
	enabledJobs: readonly Job[];
};

/**
 * The shift record for one nurse: the grid's prepared nurse with the picked hours, the "Shift details"
 * charge / training status and the step-1 job.
 */
export const buildPreparedShift = (deps: PreparedShiftDeps, target: AddStaffTarget, nurse: Nurse): NurseInShift => {
	const { window, weekIndex, dayNo, shiftNo, room, jobId, assignment } = target;
	// No shift type here: the shift takes its row's own, which a week missing a shift type's row can't give by position.
	const shiftTimes = { start: window.start, end: window.end, date: window.start };
	const { date, shiftType } = scheduleSlotDay(deps.weeks, { weekIndex, shiftIndex: shiftNo, dayIndex: dayNo });
	const prepared = deps.prepareNurseShift({
		nurse,
		date,
		shiftType,
		location: room,
		addedFromActual: false,
		requestId: undefined,
		shiftTimes,
		nurseInShiftId: undefined,
		shiftBonus: undefined,
		isDraftMode: false,
	});
	const job = jobId ? deps.enabledJobs.find((candidate) => candidate.id === jobId) : undefined;
	return {
		...prepared,
		charge: assignment.charge,
		trainingStatus: assignment.trainingStatus,
		...(jobId ? { jobId } : {}),
		// Keep the local copy's job label in step with the job the shift is booked under.
		...(job ? { jobName: job.displayName } : {}),
	};
};

type AddNurseEventArgs = {
	target: AddStaffTarget;
	/** `getShiftDate(...)` of the slot, already resolved by the caller. */
	date: string;
	nurse: Nurse;
	/** How many staff were picked, and how many went out in this one publish. */
	selectedCount: number;
	batchSize: number;
};

/** The "Staffing - add nurse" properties — one event per added nurse, whatever the batch size. */
export const buildAddNurseEventProps = ({ target, date, nurse, selectedCount, batchSize }: AddNurseEventArgs) => ({
	source: ADD_STAFF_ANALYTICS_SOURCE,
	week_index: target.weekIndex,
	day_no: target.dayNo,
	shift_no: target.shiftNo,
	date,
	nurse_id: nurse.id,
	// Analytics keeps the stable "First Last" form, whatever order the department displays.
	nurse_name: formatPersonFullName(nurse, false),
	role: nurse.role,
	location: target.room,
	make_charge: target.assignment.charge,
	training_status: target.assignment.trainingStatus !== NurseTrainingStatus.Regular ? target.assignment.trainingStatus : undefined,
	selected_count: selectedCount,
	batch_size: batchSize,
});

/** When the "this open shift is now redundant" offer is off for an add. */
export type RedundantOpenShiftGate = {
	skipRedundantOpenShiftSuggestion: boolean;
	isActual: boolean;
	isDraftMode: boolean;
	/** Hospital adds land pending, not committed, so like drafts they don't retire a need yet. */
	isHospital: boolean;
};

export const shouldOfferRedundantOpenShift = ({ skipRedundantOpenShiftSuggestion, isActual, isDraftMode, isHospital }: RedundantOpenShiftGate): boolean =>
	!skipRedundantOpenShiftSuggestion && !isActual && !isDraftMode && !isHospital;

type RedundantMatchContext = { departmentId: string; jobManagementEnabled: boolean };

/** The open shift adding `nurse` to `target` would make redundant, if any. */
export const findRedundantOpenShiftFor = (
	requests: ShiftRequest[],
	target: AddStaffTarget,
	nurse: Nurse,
	ctx: RedundantMatchContext,
): OpenShiftRequest | undefined =>
	findRedundantOpenShift(requests, {
		departmentId: ctx.departmentId,
		shiftStart: target.window.start,
		shiftEnd: target.window.end,
		location: target.room,
		jobId: target.jobId,
		role: nurse.role,
		jobManagementEnabled: ctx.jobManagementEnabled,
		isCharge: target.assignment.charge,
	});

/**
 * The open-shift offers a multi-add makes redundant, allocated against capacity: each added nurse fills
 * one slot of the request it matches, and a request takes no more nurses than it still needs
 * (`needed_nurses`) — once it is full, the next nurse of that job / role goes to the next matching
 * request, or to none. One entry per filled slot, in pick order, so a request needing 2 that two nurses
 * fill is listed twice: the prompt re-reads the live request each time and every confirm retires one slot.
 * Resolve BEFORE the add: adding refreshes the requests this reads.
 */
export const collectRedundantOpenShifts = (
	requests: ShiftRequest[],
	target: AddStaffTarget,
	nurses: readonly Nurse[],
	ctx: RedundantMatchContext,
): OpenShiftRequest[] => {
	const offers: OpenShiftRequest[] = [];
	const isFull = (request: ShiftRequest) => isOpenShiftRequest(request) && offers.filter((offer) => offer.id === request.id).length >= request.needed_nurses;
	for (const nurse of nurses) {
		const match = findRedundantOpenShiftFor(
			requests.filter((request) => !isFull(request)),
			target,
			nurse,
			ctx,
		);
		if (match) offers.push(match);
	}
	return offers;
};
